import React, { useEffect, useRef, useState } from 'react';
import { Container, Row, Col, Button, Form } from 'react-bootstrap';
import { X } from 'react-bootstrap-icons';
import './MessageFilters.css';

const DEBOUNCE_DELAY = 250;

const MessageFilters = ({filters, getMessages, clearMessages, setFilters, saveFilters, clearFilters}) => {
  const isMountedRef = useRef(null);

  const [filtersLoading, setFiltersLoading] = useState(true);
  const [dynamicFields, setDynamicFields] = useState(['']);
  const [localFilters, setLocalFilters] = useState(filters);

  useEffect(() => {
    const handler = setTimeout(() => {
      setFilters(localFilters);
      clearMessages();
    }, DEBOUNCE_DELAY);

    return () => clearTimeout(handler);
  }, [localFilters, setFilters, clearMessages]);

  useEffect(() => {
    isMountedRef.current = true;
    
    filters.length !== 0 && setFiltersLoading(false);
    setLocalFilters(filters);
    return () => (isMountedRef.current = false);
  }, [filtersLoading, filters]);
  
  const handleDynamicFieldChange = (index, value) => {
    const updatedFields = [...dynamicFields];
    updatedFields[index] = value;
    setDynamicFields(updatedFields);
    setLocalFilters({ ...localFilters, provider: updatedFields });
  };

  const handleAddField = () => {
    const updatedFields = [...dynamicFields, ''];
    setDynamicFields(updatedFields);
    setLocalFilters({ ...localFilters, provider: updatedFields });
  };

  const handleRemoveField = (index) => {
    const updatedFields = dynamicFields.filter((_, i) => i !== index);
    setDynamicFields(updatedFields);
    setLocalFilters({ ...localFilters, provider: updatedFields });
  };

  const handleChange = (e) => {
    setLocalFilters({
      ...localFilters,
      [e.target.name]: e.target.value,
    });
  };

  const handleSelectChange = (e) => {
    const selectedOptions = Array.from(e.target.selectedOptions, (option) => option.value);
    setLocalFilters({ ...localFilters, [e.target.name]: selectedOptions });
  };

  const handleShowMessages = () => {
    console.log("handleShowMessages()");
    //This needs to set the user preferences so there are no hidden messages
    let newFilters = { ...filters, showHiddenMessages: true, hideCareTeam: false, hideNonCareTeam: false };
    setFilters(newFilters);
    clearMessages();
  };

  const handleHideCareTeamMessages = () => {
    console.log("handleHideCareTeamMessages()");
    let newFilters = { ...filters, hideCareTeam: true, hideNonCareTeam: false };
    setFilters(newFilters);
    clearMessages();
  };

  const handleHideNonCareTeamMessages = () => {
    console.log("handleHideNonCareTeamMessages()");
    let newFilters = { ...filters, hideCareTeam: false, hideNonCareTeam: true };
    setFilters(newFilters);
    clearMessages();
  };

  const handleSaveFilters = () => {
    console.log("handleSaveFilters()");
    saveFilters(filters);
  };

  const handleClearFilters = () => {
    console.log("handleClearFilters()");
    clearFilters();

    //This also needs to trigger the messages to be retrieved
    clearMessages();
  };

  return (
    <>
      <div>
        <Container fluid>
        <div className="MessageFiltersGap"/>
          <Row className='MessageFiltersHeader MuiTableHead-root'>
            <Col>
              <span className='MessageFiltersHeader' >Filters</span>
            </Col>
          </Row>
          
          <Row className='MessageFiltersRow'>
              <Button className='MessageFilters' onClick={handleSaveFilters}>
                <span>Save Filters</span>
              </Button>
              <Button className='MessageFilters' onClick={handleClearFilters}>
                <span>Clear Filters</span>
              </Button>
          </Row>
          
          <Row className='MessageFiltersRow'>
              <Button className='MessageFilters' onClick={handleShowMessages}>
                <span>Show All Hidden Messages</span>
              </Button>
          </Row>

          <Row>
            <Col className='MessageFiltersCol'>
              <span className='MessageFilters' >Search</span>
              <Form>
              <Form.Group as={Row} className="mb-3 MessageFiltersTextControl" controlId="patientFirstName">
                <Form.Label column sm="4">
                  Patient First Name
                </Form.Label>
                <Col sm="8">
                  <Form.Control plaintext name="patientFirstName" value={localFilters.patientFirstName} onChange={handleChange} />
                </Col>
              </Form.Group>

              <Form.Group as={Row} className="mb-3 MessageFiltersTextControl" controlId="patientLastName">
                <Form.Label column sm="4">
                  Patient Last Name
                </Form.Label>
                <Col sm="8">
                  <Form.Control plaintext name="patientLastName" value={localFilters.patientLastName} onChange={handleChange} />
                </Col>
              </Form.Group>

              <Form.Group as={Row} className="mb-3 MessageFiltersTextControl" controlId="patientDob">
                <Form.Label column sm="4">
                  DOB
                </Form.Label>
                <Col sm="8">
                  <Form.Control plaintext name="patientDob" value={localFilters.patientDob} onChange={handleChange} />
                </Col>
              </Form.Group>

              <Form.Group as={Row} className="mb-3 MessageFiltersTextControl" controlId="providerFirstName">
                <Form.Label column sm="4">
                  Provider First Name
                </Form.Label>
                <Col sm="8">
                  <Form.Control plaintext name="providerFirstName" value={localFilters.providerFirstName} onChange={handleChange} />
                </Col>
              </Form.Group>

              <Form.Group as={Row} className="mb-3 MessageFiltersTextControl" controlId="providerLastName">
                <Form.Label column sm="4">
                  Provider Last Name
                </Form.Label>
                <Col sm="8">
                  <Form.Control plaintext name="providerLastName" value={localFilters.providerLastName} onChange={handleChange}  />
                </Col>
              </Form.Group>

            </Form>
            </Col>
          </Row>

          <Row>
            <Col className='MessageFiltersCol'>
              <span className='MessageFilters' >Date Range</span>
              <Form.Control as="select" multiple value={localFilters.dateRange} onChange={handleSelectChange} name="dateRange">
                <option className='MessageFiltersOption' value="1">Yesterday</option>
                <option className='MessageFiltersOption' value="7">Last 7 Days</option>
                <option className='MessageFiltersOption' value="30">Last 30 Days</option>
              </Form.Control>    
            </Col>
          </Row>

          <Row>
            <Col className='MessageFiltersCol'>
              <span className='MessageFilters' >Message Type</span>
              <Form.Control as="select" multiple value={localFilters.messageType} onChange={handleSelectChange} name="messageType">
                <option className='MessageFiltersOption' value="adt">ADT</option>
                <option className='MessageFiltersOption' value="lab">LAB</option>
                <option className='MessageFiltersOption' value="rad">RAD</option>
                <option className='MessageFiltersOption' value="trans">TRANS</option>
              </Form.Control>
            </Col>
          </Row>

          {/*TEMPORARY <Row className='MessageFiltersRow'>
              <Button className='MessageFilters' onClick={handleHideCareTeamMessages}>
                <span>Hide All Care Team Messages</span>
              </Button>
          </Row>

          <Row className='MessageFiltersRow'>
              <Button className='MessageFilters' onClick={handleHideNonCareTeamMessages}>
                <span>Hide Non Care Team Messages</span>
              </Button>
          </Row>*/}

          <Row>
            <Col className="MessageFiltersCol">
              <span className="MessageFilters">Provider NPI Search</span>
              {dynamicFields.map((field, index) => (
                <Form.Group as={Row} className="mb-3 MessageFiltersTextControl" key={index}>
                  <Col sm="8">
                    <Form.Control
                      type="text"
                      value={field}
                      onChange={(e) => handleDynamicFieldChange(index, e.target.value)}
                      placeholder={`Enter value for NPI ${index + 1}`}
                    />
                  </Col>
                  <Col sm="auto">
                  <Button
                    variant="danger"
                    onClick={() => handleRemoveField(index)}
                    className="MessageFiltersRemoveButton"
                  >
                    <X />
                  </Button>
                  </Col>
                </Form.Group>
              ))}
              <Button variant="primary" onClick={handleAddField} className="MessageFiltersAddButton">
                Add Field
              </Button>
            </Col>
          </Row>
        </Container>
      </div>
    </>
  );
};

export default MessageFilters;
